/**
 * Drag handle on the left edge of the DraftSheet. Pointer-driven
 * resize between [DRAFT_PANEL_MIN, DRAFT_PANEL_MAX]. Uses pointer
 * capture so the drag survives the cursor leaving the handle.
 *
 * Visual: a 4px-wide hit zone with a centered phosphor pill that
 * brightens on hover. Cursor flips to col-resize once active.
 */
import { Box } from "@mui/material";
import { useDraftStore } from "@stores/draftStore";
import type { PointerEvent } from "react";

export function DraftResizeHandle() {
  const setPanelWidth = useDraftStore((s) => s.setPanelWidth);

  function handlePointerDown(e: PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);

    function move(
      this: HTMLDivElement,
      ev: PointerEvent | globalThis.PointerEvent,
    ) {
      // Width = (window right edge) - (cursor x). Sheet is anchored
      // right, so as the cursor moves left the panel grows.
      const next = window.innerWidth - (ev as globalThis.PointerEvent).clientX;
      setPanelWidth(next);
    }
    function up(
      this: HTMLDivElement,
      ev: PointerEvent | globalThis.PointerEvent,
    ) {
      e.currentTarget.releasePointerCapture(
        (ev as globalThis.PointerEvent).pointerId,
      );
      e.currentTarget.removeEventListener("pointermove", move as never);
      e.currentTarget.removeEventListener("pointerup", up as never);
    }

    e.currentTarget.addEventListener("pointermove", move as never);
    e.currentTarget.addEventListener("pointerup", up as never);
  }

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      onPointerDown={handlePointerDown}
      sx={(t) => ({
        position: "absolute",
        left: -2,
        top: 0,
        bottom: 0,
        width: 6,
        cursor: "col-resize",
        zIndex: 1,
        // Centered pill — 1px tall by 24px wide, brightens on hover.
        "&::after": {
          content: '""',
          position: "absolute",
          left: 2,
          top: "50%",
          transform: "translateY(-50%)",
          width: 2,
          height: 32,
          borderRadius: 4,
          background: t.palette.border.default,
          transition: `background ${t.motion.fast}`,
        },
        "&:hover::after, &:active::after": {
          background: t.palette.accent.main,
          boxShadow: t.palette.accent.glow,
        },
      })}
    />
  );
}
